import { c as _c } from "react/compiler-runtime";
import "client-only";
import { useEffectEvent, useRef } from "react";
import { useDocumentEvent } from "@/hooks/browser/use-document-event.hook";
import { useWindowEvent } from "@/hooks/browser/use-window-event.hook";
import { usePathnameChange } from "@/hooks/navigation/use-pathname-change.hook";
import type { PageLifeCycleEvent } from "@/vendors/Snowplow/types/observation-boundary-tracker.types";
type PageLifecycleCallback = (event: PageLifeCycleEvent) => void;

/**
 * Fires a callback on page lifecycle events with automatic deduplication.
 *
 * Handles four types of events:
 * - `"pagehide"` - Tab closing or navigating away (bfcache)
 * - `"beforeunload"` - Page about to unload
 * - `"visibilitychange"` - Tab became hidden
 * - `"navigation"` - Next.js client-side navigation
 *
 * ## Deduplication (latch pattern)
 *
 * Uses a latch (`hasFiredRef`) to prevent multiple callbacks for the same
 * "exit" scenario. For example, closing a tab might trigger both
 * `visibilitychange` and `pagehide` - only the first one fires the callback.
 *
 * The latch resets when:
 * - User navigates to a new page (staying in the app)
 * - Tab becomes visible again after being hidden
 */
export function usePageLifecycle(callback) {
  const $ = _c(10);
  const hasFiredRef = useRef(false);
  let t0;
  if ($[0] !== callback) {
    t0 = event => {
      if (hasFiredRef.current) {
        return;
      }
      hasFiredRef.current = true;
      callback(event);
    };
    $[0] = callback;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleLifecycleEvent = useEffectEvent(t0);
  let t1;
  if ($[2] !== callback) {
    t1 = () => {
      hasFiredRef.current = false;
      callback("navigation");
    };
    $[2] = callback;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  usePathnameChange(t1);
  let t2;
  if ($[4] !== handleLifecycleEvent) {
    t2 = () => handleLifecycleEvent("pagehide");
    $[4] = handleLifecycleEvent;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useWindowEvent("pagehide", t2);
  let t3;
  if ($[6] !== handleLifecycleEvent) {
    t3 = () => handleLifecycleEvent("beforeunload");
    $[6] = handleLifecycleEvent;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useWindowEvent("beforeunload", t3);
  let t4;
  if ($[8] !== handleLifecycleEvent) {
    t4 = () => {
      if (document.visibilityState === "hidden") {
        handleLifecycleEvent("visibilitychange");
      } else {
        hasFiredRef.current = false;
      }
    };
    $[8] = handleLifecycleEvent;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useDocumentEvent("visibilitychange", t4);
}